import { useEffect } from 'react';
import {
	getSessionIdFromToken,
	getUserIdFromToken
} from 'features/communication-bridge/tokenStore';
import { getDeviceId } from 'features/communication-bridge/deviceIdStore';
import {
	useCommunicationBridge,
	type MessageFromShell
} from 'features/communication-bridge/CommunicationBridgeProvider';
import { configureDatadogUser } from 'datadog';
import { configureSentryUser } from 'sentry';

/**
 * Tags Sentry and Datadog with the Ledidi user, session and device ids. Those
 * are the three Mixpanel and the backend audit log already use, so an error
 * says which person hit it and lines up with what the shell reports.
 */
export const useErrorReportingIdentification = () => {
	const { addEventListener, removeEventListener } = useCommunicationBridge();

	useEffect(() => {
		const identify = () => {
			const userId = getUserIdFromToken();
			if (!userId) return;

			const user = {
				userId,
				sessionId: getSessionIdFromToken(),
				deviceId: getDeviceId()
			};

			configureSentryUser(user);
			configureDatadogUser(user);
		};

		identify();

		// The device id travels in its own message. The token is what lets this
		// tree render, so the device id can land after the first tag — tag again
		// rather than rely on the shell sending the two in a given order.
		const onMessageFromShell = (message: MessageFromShell) => {
			if (message.type !== 'setDeviceId') return;
			identify();
		};

		addEventListener(onMessageFromShell);
		return () => removeEventListener(onMessageFromShell);
	}, [addEventListener, removeEventListener]);
};
